import { z } from "zod";

// Schema para coordenadas geográficas
const coordinatesSchema = z.object({
  0: z.number().min(-180).max(180), // Longitud
  1: z.number().min(-90).max(90), // Latitud
});

// Schema para geometría
const geometrySchema = z.object({
  coordinates: coordinatesSchema,
  type: z.literal("point"),
});

// Schema para dirección
const addressSchema = z.object({
  street: z.string().max(500, "La dirección es demasiado larga"),
  city: z.string().max(100, "La ciudad es demasiado larga"),
  state: z.string().max(100, "El estado es demasiado largo"),
  country: z.string().max(100, "El país es demasiado largo"),
  geometry: geometrySchema.optional(),
});

// Schema para notificaciones
const notificationSchema = z.object({
  email: z.boolean().default(true),
  push: z.boolean().default(false),
  sms: z.boolean().default(false),
  ws: z.boolean().default(false),
});

// Schema para redes sociales
const socialNetworkSchema = z.object({
  name: z.enum(["facebook", "twitter", "linkedin", "instagram", "web_site"]),
  url: z.string().max(200, "La URL es demasiado larga"),
  displayed: z.boolean().default(true),
});

// Schema para horario de atención
const timetableSchema = z.object({
  weekday: z.array(z.string()).optional(),
});

const attentionSchema = z.object({
  timetable: z.array(timetableSchema).optional(),
});

const scheduleSchema = z.object({
  attention: attentionSchema.optional(),
});

// Schema principal para institución (compatible con userSchema)
export const institutionSchema = z.object({
  uid: z.string().optional(), // Firebase Auth UID
  name: z
    .string()
    .min(1, "El nombre es requerido")
    .max(200, "El nombre es demasiado largo"),
  email: z
    .string()
    .min(1, "El correo electrónico es requerido")
    .email("Formato de correo electrónico inválido"),
  email_support: z
    .string()
    .max(200, "El correo de soporte es demasiado largo")
    .optional(),
  phone: z.array(z.string().max(20, "El número de teléfono es demasiado largo")),
  phone_support: z
    .string()
    .max(20, "El teléfono de soporte es demasiado largo")
    .optional(),
  address: addressSchema,
  description: z.string().max(2000, "La descripción es demasiado larga").optional(),
  category: z.string().max(100, "La categoría es demasiado larga"),
  type: z.literal("institution"), // Siempre será institution
  active: z.boolean().default(true),
  active_imaging: z.boolean().default(true),
  admin_imaging: z.boolean().default(false),
  block_upload: z.boolean().default(false),
  customized: z.boolean().default(false),
  edit_report_vm: z.boolean().default(false),
  days_link: z.number().int().min(1).max(365).default(31),
  dental_images_only: z.boolean().nullable().optional(),
  domain: z.string().nullable().optional(),
  id_tax: z.string().max(50, "El ID fiscal es demasiado largo").optional(),
  logo_email: z.string().nullable().optional(),
  template_name: z
    .string()
    .max(100, "El nombre de la plantilla es demasiado largo")
    .default("default"),
  timezone: z
    .string()
    .max(50, "La zona horaria es demasiado larga")
    .default("America/Caracas"),
  notification: notificationSchema.optional(),
  schedule: scheduleSchema.optional(),
  social_networks: z.array(socialNetworkSchema).optional(),
  attendances: z.array(z.string()).optional(),
  status: z.enum(["active", "inactive", "pending", "suspended"]).default("active"),
  role: z.enum(["admin", "editor", "user"]).default("user"),
  avatar: z.string().url("URL de avatar inválida").optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  last_login: z.string().optional(),
});

// Schema para crear institución
export const createInstitutionSchema = institutionSchema.omit({
  uid: true,
  created_at: true,
  updated_at: true,
  last_login: true,
});

// Schema para actualizar institución
export const updateInstitutionSchema = institutionSchema.partial().omit({
  uid: true,
  created_at: true,
  updated_at: true,
  last_login: true,
});

// Schema para filtros de institución
export const institutionFiltersSchema = z.object({
  search: z.string().optional(),
  active: z.boolean().optional(),
  category: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  status: z.enum(["active", "inactive", "pending", "suspended"]).optional(),
  sortBy: z.enum(["name", "email", "active", "created_at", "last_login"]).optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

// Schema para respuesta paginada de instituciones
export const institutionsResponseSchema = z.object({
  data: z.array(institutionSchema),
  pagination: z.object({
    page: z.number(),
    limit: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
  message: z.string().optional(),
  success: z.boolean(),
});

// Tipos inferidos de los schemas
export type Institution = z.infer<typeof institutionSchema>;
export type CreateInstitutionData = z.infer<typeof createInstitutionSchema>;
export type UpdateInstitutionData = z.infer<typeof updateInstitutionSchema>;
export type InstitutionFilters = z.infer<typeof institutionFiltersSchema>;
export type InstitutionsResponse = z.infer<typeof institutionsResponseSchema>;
export type Address = z.infer<typeof addressSchema>;
export type Notification = z.infer<typeof notificationSchema>;
export type SocialNetwork = z.infer<typeof socialNetworkSchema>;
export type Geometry = z.infer<typeof geometrySchema>;
export type Coordinates = z.infer<typeof coordinatesSchema>;
